import React, { useEffect, useState } from 'react';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import ShipToAddressForm from '../components/ShippingAddressForm';
import ProjectSelector, { SelectorOption } from '../components/ProjectSelector';
import { ProjectModel } from '../../../interfaces/project.interface';
import { setDefaultProject } from '../services/Teacher.service';
import { getTeacherClassroomProjects } from '../../../store/thunks/user-teacher.thunk';
import { Toast } from '../../../components/Common/Toast';

export interface TeacherLibraryProps {}

const TeacherClassroomSettings = ({}: TeacherLibraryProps) => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const activeClassroomProjects = useSelector((state: RootState) => state.user.activeClassroomProjects);
  const activeClassroom = useSelector((state: RootState) => state.user.activeClassroom);

  const [selectedProject, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProjectData, setSelectedProjectData] = useState<ProjectModel | null>(null);
  const [isSettingDefault, setIsSettingDefault] = useState<boolean>(false);

  useEffect(() => {
    if (!activeClassroomProjects?.length) return;
    const activeProject =
      activeClassroomProjects?.find((project) => project.id === selectedProject) ||
      activeClassroomProjects?.find((item) => item.isDefaultProject === true) ||
      activeClassroomProjects[0];
    setSelectedProjectId(activeProject?.id!);
    setSelectedProjectData(activeProject);
  }, [activeClassroomProjects, selectedProject]);

  const handleSetDefaultProject = async () => {
    if (!selectedProjectData?.id || !activeClassroom?.id) {
      Toast('Please select a project first', true);
      return;
    }

    setIsSettingDefault(true);
    try {
      await setDefaultProject(activeClassroom.id, selectedProjectData.id, () => {
        // Refresh projects to get updated default status
        dispatch(getTeacherClassroomProjects(activeClassroom.id));
      });
    } catch (error) {
      console.error('Error setting default project:', error);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const isCurrentProjectDefault = selectedProjectData?.isDefaultProject;

  return (
    <div className="min-h-[calc(100vh-10rem)] flex flex-col font-sofiaSans max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* General Settings Section */}
      <div className="mt-8 w-full flex flex-col items-center">
        <h1 className="text-xl sm:text-2xl w-full pl-4 sm:pl-8 font-bold text-secondaryStoryBlueText text-left">
          General Settings
        </h1>

        <div className="bg-white w-full flex justify-center rounded-[24px] sm:rounded-[36px] p-4 sm:p-8 mt-4 shadow-sm">
          <div className="flex flex-col mx-auto w-full max-w-sm">
            <h2 className="text-lg sm:text-xl font-bold mb-2">What is your school's ship-to address?</h2>
            <p className="text-secondaryStoryBlueText mb-4 sm:mb-6 text-sm sm:text-base">
              We require this so we know where to ship the students' published books.
            </p>

            <ShipToAddressForm
              user={user}
              hideTitle={true}
              submitButtonTitle={'Update'}
              onSaved={() => {}}
              useValidation={true}
            ></ShipToAddressForm>
          </div>
        </div>
      </div>

      {/* Project settings section */}
      <div className="mt-8 sm:mt-12 flex flex-col w-full">
        <h1 className="text-xl sm:text-2xl w-full pl-4 sm:pl-8 font-bold text-secondaryStoryBlueText text-left">
          Project Settings
        </h1>

        <div className="bg-white mt-6 shadow-sm rounded-[24px] sm:rounded-[36px] p-6 sm:p-8">
          <div className="flex flex-col">
            <h2 className="text-lg sm:text-xl font-bold mb-4 text-left">Active project for this class</h2>
            <p className="text-secondaryStoryBlueText mb-6 text-sm sm:text-base text-left">
              All new student books will be added to this project's library.
            </p>

            <div className="flex flex-row items-center gap-2 mb-4">
              <div className="flex items-center flex-nowrap whitespace-nowrap font-bold pr-1.5 text-sm sm:text-base">
                Active project -
              </div>
              <ProjectSelector
                placeholder="Choose project"
                selectedItem={selectedProject}
                onChange={(data: SelectorOption) => setSelectedProjectId(data?.value)}
                options={
                  activeClassroomProjects?.map((item) => ({
                    title: item.name || '',
                    value: item.id || '',
                    isDefaultProject: item.isDefaultProject,
                  })) || []
                }
                addNewItemTitle={'Add new project'}
              ></ProjectSelector>
            </div>

            <div className="flex justify-start">
              <button
                onClick={handleSetDefaultProject}
                disabled={isSettingDefault || isCurrentProjectDefault}
                className={`px-6 py-2 rounded-lg text-sm font-bold transition-colors bg-blue-500 text-white hover:bg-blue-600
                ${isSettingDefault ? 'opacity-50 cursor-not-allowed' : ''}
                ${isCurrentProjectDefault ? 'opacity-75 cursor-not-allowed hover:bg-blue-500' : ''}
                `}
              >
                {isSettingDefault ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white mt-8 mb-12 p-6 sm:p-10 pt-4 sm:pt-6 shadow-sm rounded-[24px] sm:rounded-[36px]">
          <h2 className="text-lg sm:text-xl font-bold mb-4 text-left">Danger zone</h2>
          <button className="flex items-center gap-2 text-storyseconderror font-bold border border-storyseconderror rounded-lg pl-2 pr-3 sm:pr-4 py-1 hover:bg-red-50 text-sm sm:text-base">
            <img
              src="/trash_icon_transparent.png"
              alt="print"
              className="text-xl text-storyblue cursor-pointer w-6 h-6 sm:w-8 sm:h-8"
            />
            Archive project
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherClassroomSettings;
